import ListItem from "@/components/ListItem/ListItem";
import TextField from "@/components/TextField";
import Icon from "@/components/Icon";
import { InteractionInput } from "@/components/interactions/types";
import { useEffect, useState } from "react";
import { AlgoliaSearchResults, type PersonHit, getPersonDisplayName } from "@/components/AlgoliaSearch";
import { renderCreateInteractionPersonHit } from "./createInteractionFuzzyHitRenderer";
import { useCreateInteractionFuzzySearch } from "./useCreateInteractionFuzzySearch";

type argumentsType = {
  setPeoplePGAId: (value: string) => void;
  setSearchedForPerson: (value: boolean) => void;
  setVariables: (value: InteractionInput) => void;
  variables: InteractionInput;
  disabled?: boolean;
  setValue?: ((name: "interactionPerson", value: string) => void) &
    ((name: "interactionPrivacy", value: boolean) => void);
};

export const SinglePerson = ({
  setPeoplePGAId,
  setSearchedForPerson,
  setVariables,
  variables,
  disabled,
  setValue
}: argumentsType) => {
  const [selectedPerson, setSelectedPerson] = useState<{
    name: string;
    profilePhoto?: string;
    facilityName?: string;
    sectionName?: string;
  } | null>(null);

  const {
    query: personQuery,
    displayValue,
    results: personResults,
    isLoading: personLoading,
    failed: personFailed,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear: handlePersonClear,
    handleItemSelected,
    setIsFocused,
    setDisplayValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useCreateInteractionFuzzySearch({
    entity: "people",
    getDisplayValue: getPersonDisplayName,
    onSelect: (hit: PersonHit) => {
      const pgaId = hit.pga_id || hit.legacy_id;
      if (pgaId) {
        const fullName = getPersonDisplayName(hit);
        setSelectedPerson({
          name: fullName,
          profilePhoto: hit.profile_photo,
          facilityName: hit.facility_name,
          sectionName: hit.section_name
        });
        setValue?.("interactionPerson", fullName);
        setPeoplePGAId(pgaId);
        setSearchedForPerson(true);
      }
    }
  });

  const hasActivePersonQuery = personQuery.trim() !== "";

  useEffect(() => {
    if (!selectedPerson) {
      focusInput();
    }
  }, [selectedPerson, focusInput]);

  useEffect(() => {
    if (displayValue) {
      setDisplayValue(undefined);
    }
  }, [displayValue, setDisplayValue]);

  const handleClearPerson = () => {
    handlePersonClear();
    setDisplayValue(undefined);
    setSelectedPerson(null);
    setValue?.("interactionPerson", "");
    setPeoplePGAId("");
    setSearchedForPerson(false);
    setVariables({ ...variables, participantId: null });
    requestAnimationFrame(() => focusInput());
  };

  const personTrailingIcon =
    displayValue || personQuery ? (
      <button
        type="button"
        className="cursor-pointer flex items-center h-full"
        onClick={handleClearPerson}
        aria-label="Clear search query"
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  return (
    <>
      {!selectedPerson && (
        <div ref={searchBoxRef}>
          <TextField
            label="Person"
            placeholder="Search for a person"
            value={displayValue || personQuery}
            onChangeText={(value) => {
              setDisplayValue(undefined);
              handleQueryChange(value);
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            leadingIcon="search"
            trailingIcon={personTrailingIcon}
            variant="outlined"
            className="w-full"
            supportingText="Search for a person to create an interaction"
            disabled={disabled}
            data-testid="single-person-search-input"
          />
        </div>
      )}

      {selectedPerson && (
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <ListItem
              compact
              useAvatar
              picture={selectedPerson.profilePhoto}
              headline={selectedPerson.name}
              supportingText={
                [selectedPerson.facilityName, selectedPerson.sectionName].filter(Boolean).join("\n") || undefined
              }
            />
          </div>
          <button
            type="button"
            className="cursor-pointer flex items-center shrink-0 p-1 rounded-full hover:bg-sys-brand-surface-container-high transition-colors"
            onClick={handleClearPerson}
            aria-label="Remove selected person"
            disabled={disabled}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
      )}

      {hasActivePersonQuery && (
        <AlgoliaSearchResults
          results={personResults}
          query={personQuery}
          isLoading={personLoading}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          handleItemSelected={handleItemSelected}
          itemRefs={itemRefs}
          resultsContainerRef={resultsContainerRef}
          renderHit={renderCreateInteractionPersonHit}
          noResultsMessage={personFailed ? "Couldn't load people" : "No results found"}
          className="mt-4"
        />
      )}
    </>
  );
};
